/**
 * Gössl Wishlist + Outfit Groups
 * localStorage for all visitors, syncs to Xano for logged-in customers.
 *
 * APIs:
 *   window.Goessl.wishlist  — flat wishlist (array of handles)
 *   window.Goessl.outfits   — outfit groups (array of outfit objects)
 */
(function() {
  'use strict';

  var STORAGE_KEY = 'goessl_wishlist';
  var OUTFITS_KEY = 'goessl_outfits';
  // 2026-06-10: repointed from Xano to the CF Worker (Xano retires with the
  // Intex sunset). Identical request/response contract, KV-backed server-side.
  var XANO_BASE = 'https://goessl-cron-jobs.asksj.workers.dev';
  var syncDebounceTimer = null;
  var serverSynced = false;

  // ═══════════════════════════════════════════
  //  WISHLIST (flat list of product handles)
  // ═══════════════════════════════════════════

  function getItems() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
    catch (e) { return []; }
  }

  function saveItems(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items: items } }));
  }

  function hasItem(handle) {
    return getItems().indexOf(handle) !== -1;
  }

  function addItem(handle) {
    var items = getItems();
    if (items.indexOf(handle) === -1) {
      items.push(handle);
      saveItems(items);
      debouncedServerSync();
    }
  }

  function removeItem(handle) {
    var items = getItems().filter(function(h) { return h !== handle; });
    saveItems(items);
    debouncedServerSync();
  }

  function toggleItem(handle) {
    if (hasItem(handle)) { removeItem(handle); return false; }
    else { addItem(handle); return true; }
  }

  function getCount() { return getItems().length; }

  // ═══════════════════════════════════════════
  //  OUTFIT GROUPS
  // ═══════════════════════════════════════════

  function getOutfits() {
    try { return JSON.parse(localStorage.getItem(OUTFITS_KEY)) || []; }
    catch (e) { return []; }
  }

  function saveOutfits(outfits) {
    localStorage.setItem(OUTFITS_KEY, JSON.stringify(outfits));
    document.dispatchEvent(new CustomEvent('outfits:updated', { detail: { outfits: outfits } }));
    debouncedServerSync();
  }

  function generateId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return 'outfit-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
  }

  function createOutfit(name, items, gender) {
    var outfits = getOutfits();
    var outfit = {
      id: generateId(),
      name: name || 'Mein Outfit',
      created_at: new Date().toISOString(),
      gender: gender || null,
      items: items || []
    };
    outfits.unshift(outfit);
    saveOutfits(outfits);
    return outfit;
  }

  function getOutfit(outfitId) {
    return getOutfits().find(function(o) { return o.id === outfitId; }) || null;
  }

  function updateOutfit(outfitId, updates) {
    var outfits = getOutfits();
    for (var i = 0; i < outfits.length; i++) {
      if (outfits[i].id === outfitId) {
        Object.keys(updates).forEach(function(key) {
          outfits[i][key] = updates[key];
        });
        break;
      }
    }
    saveOutfits(outfits);
  }

  function deleteOutfit(outfitId) {
    var outfits = getOutfits().filter(function(o) { return o.id !== outfitId; });
    saveOutfits(outfits);
  }

  function addToOutfit(outfitId, item) {
    // item: { handle, variant_id, category_slot, position, scale }
    var outfits = getOutfits();
    for (var i = 0; i < outfits.length; i++) {
      if (outfits[i].id === outfitId) {
        // If category_slot is set and not 'accessoires', replace existing in that slot
        if (item.category_slot && item.category_slot !== 'accessoires') {
          outfits[i].items = outfits[i].items.filter(function(existing) {
            return existing.category_slot !== item.category_slot;
          });
        }
        outfits[i].items.push(item);
        break;
      }
    }
    saveOutfits(outfits);
  }

  function removeFromOutfit(outfitId, handle) {
    var outfits = getOutfits();
    for (var i = 0; i < outfits.length; i++) {
      if (outfits[i].id === outfitId) {
        outfits[i].items = outfits[i].items.filter(function(it) {
          return it.handle !== handle;
        });
        break;
      }
    }
    saveOutfits(outfits);
  }

  function addOutfitToCart(outfitId) {
    var outfit = getOutfit(outfitId);
    if (!outfit || !outfit.items.length) return Promise.resolve(null);

    var cartItems = outfit.items
      .filter(function(it) { return it.variant_id; })
      .map(function(it) { return { id: it.variant_id, quantity: 1 }; });

    if (!cartItems.length) return Promise.resolve(null);

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: cartItems })
    })
    .then(function(r) { return r.json(); })
    .then(function(data) {
      document.dispatchEvent(new CustomEvent('cart:open'));
      return data;
    });
  }

  function getOutfitCount() { return getOutfits().length; }

  // ═══════════════════════════════════════════
  //  SERVER SYNC (Xano)
  // ═══════════════════════════════════════════

  function getCustomer() {
    // Namespaced location, with fallback for backwards compat
    return (window.Goessl && window.Goessl.customer) || window.__goesslCustomer || null;
  }

  function isLoggedIn() {
    return getCustomer() !== null;
  }

  function fetchServerWishlist() {
    var customer = getCustomer();
    if (!customer) return Promise.resolve(null);

    return fetch(XANO_BASE + '/storefront/wishlist?shopify_customer_id=' + customer.id, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    })
    .then(function(r) { return r.ok ? r.json() : null; })
    .catch(function() { return null; });
  }

  function pushServerWishlist() {
    var customer = getCustomer();
    if (!customer) return;

    fetch(XANO_BASE + '/storefront/wishlist', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        shopify_customer_id: customer.id,
        email: customer.email,
        handles: getItems(),
        outfits: getOutfits()
      })
    }).catch(function() { /* silent fail */ });
  }

  function debouncedServerSync() {
    if (!isLoggedIn()) return;
    clearTimeout(syncDebounceTimer);
    syncDebounceTimer = setTimeout(pushServerWishlist, 1000);
  }

  function mergeAndSync() {
    if (!isLoggedIn()) return;

    fetchServerWishlist().then(function(data) {
      if (!data) {
        if (getItems().length > 0 || getOutfits().length > 0) pushServerWishlist();
        return;
      }

      // Merge wishlist handles
      var serverHandles = data.handles || [];
      var local = getItems();
      var merged = local.slice();
      serverHandles.forEach(function(handle) {
        if (merged.indexOf(handle) === -1) merged.push(handle);
      });
      if (merged.length !== local.length) saveItems(merged);

      // Merge outfits by ID
      var serverOutfits = data.outfits || [];
      var localOutfits = getOutfits();
      var mergedOutfits = localOutfits.slice();
      var localIds = {};
      localOutfits.forEach(function(o) { localIds[o.id] = true; });
      serverOutfits.forEach(function(o) {
        if (!localIds[o.id]) mergedOutfits.push(o);
      });
      if (mergedOutfits.length !== localOutfits.length) {
        localStorage.setItem(OUTFITS_KEY, JSON.stringify(mergedOutfits));
        document.dispatchEvent(new CustomEvent('outfits:updated', { detail: { outfits: mergedOutfits } }));
      }

      // Push merged state if anything changed
      if (merged.length !== serverHandles.length || mergedOutfits.length !== serverOutfits.length) {
        pushServerWishlist();
      }

      serverSynced = true;
    });
  }

  // ═══════════════════════════════════════════
  //  UI
  // ═══════════════════════════════════════════

  function updateUI() {
    var count = getCount();

    document.querySelectorAll('[data-wishlist-count]').forEach(function(el) {
      el.textContent = count;
      el.hidden = count === 0;
    });

    document.querySelectorAll('[data-wishlist-toggle]').forEach(function(btn) {
      var handle = btn.dataset.productHandle;
      if (handle) {
        btn.classList.toggle('is-wishlisted', hasItem(handle));
        btn.setAttribute('aria-label', hasItem(handle) ? 'Von Merkliste entfernen' : 'Zur Merkliste hinzufügen');
      }
    });
  }

  function bindToggleButtons() {
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();

      var handle = btn.dataset.productHandle;
      if (!handle) return;

      var added = toggleItem(handle);
      btn.classList.toggle('is-wishlisted', added);
      btn.classList.add('wishlist-pulse');
      setTimeout(function() { btn.classList.remove('wishlist-pulse'); }, 300);
    });
  }

  // ═══════════════════════════════════════════
  //  INIT
  // ═══════════════════════════════════════════

  function init() {
    bindToggleButtons();
    updateUI();
    document.addEventListener('wishlist:updated', updateUI);

    if (isLoggedIn()) mergeAndSync();
  }

  // Expose APIs
  window.Goessl = window.Goessl || {};

  window.Goessl.wishlist = {
    getItems: getItems,
    hasItem: hasItem,
    addItem: addItem,
    removeItem: removeItem,
    toggleItem: toggleItem,
    getCount: getCount,
    updateUI: updateUI,
    isLoggedIn: isLoggedIn,
    sync: mergeAndSync
  };

  window.Goessl.outfits = {
    getOutfits: getOutfits,
    getOutfit: getOutfit,
    createOutfit: createOutfit,
    updateOutfit: updateOutfit,
    deleteOutfit: deleteOutfit,
    addToOutfit: addToOutfit,
    removeFromOutfit: removeFromOutfit,
    addOutfitToCart: addOutfitToCart,
    getCount: getOutfitCount
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
